// Fabrication-scope totals: the factual shop-vs-field crew split rendered
// under the fab-lens grid.
//
// Deliberately NO capacity ladder here. An earlier cut derived "borrowed
// self-perform" and "temps" rows from fixed pool tiers (11 → 14 → 17), but
// that composition is a week-by-week operational decision at E&W — how much
// self-perform labor can flex to fab depends on the concrete division's own
// load, and temps aren't a fixed overflow bucket. Publishing derived numbers
// that can't match reality erodes trust in the board, so composition
// (borrowed / temp headcount) will be TRACKED per job/day instead — see the
// deferred capacity-composition work (per-entry temp/borrowed counts) and the
// future Capacity Forecast view.

import type { GridColumn } from './lib/gridDates';
import { TD } from './lib/crewStyles';

const LABEL_TD: React.CSSProperties = {
  padding: '5px 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.06em', whiteSpace: 'nowrap', textAlign: 'right',
};

const NUM_TD: React.CSSProperties = {
  ...TD, padding: '5px 3px', textAlign: 'center', fontSize: 11, fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
};

/**
 * Renders INSIDE the schedule table's <tfoot> (fab scope only) so the columns
 * stay aligned with the grid above. Expects per-day totals arrays indexed like
 * `dateStrs`, projected onto the visible columns by `colValue`.
 */
export default function FabPhaseTotalsRows({
  columns, colValue, fabTotals, installTotals,
}: {
  columns: GridColumn[];
  colValue: (totals: number[], col: GridColumn) => number;
  /** Per-day fabrication-phase crew (incl. NULL-phase fallback on fab jobs). */
  fabTotals: number[];
  /** Per-day installation-phase crew. */
  installTotals: number[];
}) {
  return (
    <>
      <tr style={{ background: 'var(--ew-n-50)', borderTop: '2px solid var(--ew-n-200)' }}>
        <td colSpan={2} style={{ ...LABEL_TD, color: 'var(--ew-dark)' }}>
          <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: 'var(--ew-dark)', marginRight: 5, verticalAlign: 'middle' }} />
          Fabrication crew · shop
        </td>
        {columns.map((col, i) => {
          const v = colValue(fabTotals, col);
          return <td key={i} style={{ ...NUM_TD, borderLeft: col.isWeekStart && i > 0 ? '2px solid var(--ew-n-200)' : undefined, color: v > 0 ? 'var(--ew-dark)' : 'var(--ew-n-300)' }}>{v > 0 ? v : ''}</td>;
        })}
      </tr>
      <tr style={{ background: 'var(--ew-n-50)' }}>
        <td colSpan={2} style={{ ...LABEL_TD, color: 'var(--ew-quality)' }}>
          <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: 'var(--ew-quality)', marginRight: 5, verticalAlign: 'middle' }} />
          Installation crew · field
        </td>
        {columns.map((col, i) => {
          const v = colValue(installTotals, col);
          return <td key={i} style={{ ...NUM_TD, borderLeft: col.isWeekStart && i > 0 ? '2px solid var(--ew-n-200)' : undefined, color: v > 0 ? 'var(--ew-quality)' : 'var(--ew-n-300)' }}>{v > 0 ? v : ''}</td>;
        })}
      </tr>
    </>
  );
}
